/** Ranked honesty copy: one summary line that is always visible, the full text behind a toggle.
 *
 * Limits, bases and "where EmitIQ's part ends" are never deleted; they are ranked. The summary line
 * says the single thing a reader must not miss, the body holds the rest, and the toggle is a native
 * button with `aria-expanded` pointing at the region. The height animates with a grid-row transition
 * (no measured heights, no layout thrash); the global reduced-motion rule zeroes the transition, and a
 * collapsed body is `inert`, so its links are not in the tab order.
 */

import { CaretDown } from "@phosphor-icons/react";
import { useId, useState } from "react";
import type { ReactNode } from "react";

import styles from "./Disclosure.module.css";

export interface DisclosureProps {
  /** The one line that stays visible. */
  summary: ReactNode;
  /** The full text. */
  children: ReactNode;
  defaultOpen?: boolean;
  /** Controlled use. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  tone?: "neutral" | "info" | "warn";
  /** Name for the toggle when the summary alone is not enough: `Basis and limits`. */
  toggleLabel?: string;
  testId?: string;
}

export function Disclosure({
  summary,
  children,
  defaultOpen = false,
  open: controlled,
  onOpenChange,
  tone = "neutral",
  toggleLabel,
  testId,
}: DisclosureProps) {
  const [inner, setInner] = useState(defaultOpen);
  const open = controlled ?? inner;
  const bodyId = useId();
  const toggle = () => {
    const next = !open;
    if (controlled === undefined) setInner(next);
    onOpenChange?.(next);
  };
  return (
    <div className={`${styles.box} ${styles[tone]}`} data-testid={testId} data-open={open}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={toggle}
      >
        <span className={styles.summary}>
          {summary}
          {toggleLabel ? <span className="visuallyHidden"> ({toggleLabel})</span> : null}
        </span>
        <CaretDown size={14} weight="bold" aria-hidden="true" className={styles.caret} />
      </button>
      <div id={bodyId} className={styles.body} data-open={open} inert={!open}>
        <div className={styles.inner}>
          <div className={styles.content}>{children}</div>
        </div>
      </div>
    </div>
  );
}
